import * as React from 'react';
import { RightSmallLine } from '@mingcute/react';

import { cn } from '../lib/cn';
import { useMediaQuery } from '../hooks/use-media-query';

export interface BreadcrumbItem {
  label: React.ReactNode;
  to?: string;
  onClick?: React.MouseEventHandler<HTMLAnchorElement | HTMLSpanElement>;
}

export interface BreadcrumbsProps extends React.HTMLAttributes<HTMLElement> {
  items: BreadcrumbItem[];
  separator?: React.ReactNode;
  /** Number of items shown on mobile before collapsing the middle. */
  mobileMaxVisible?: number;
}

export const Breadcrumbs = React.forwardRef<HTMLElement, BreadcrumbsProps>(
  (
    { className, items, separator, mobileMaxVisible = 2, ...props },
    ref,
  ) => {
    const isMobile = !useMediaQuery('(min-width: 768px)');

    let displayItems: (BreadcrumbItem | { ellipsis: true })[] = items;
    if (isMobile && items.length > mobileMaxVisible + 1) {
      displayItems = [items[0]!, { ellipsis: true }, items[items.length - 1]!];
    }

    return (
      <nav
        ref={ref}
        aria-label="Breadcrumb"
        className={cn('flex w-full min-w-0', className)}
        {...props}
      >
        <ol className="flex items-center gap-1 text-sm text-[var(--color-fg-secondary)] min-w-0">
          {displayItems.map((item, idx) => {
            const isLast = idx === displayItems.length - 1;
            const sep = (
              <span
                aria-hidden
                className="inline-flex items-center text-[var(--color-fg-muted)]"
              >
                {separator ?? <RightSmallLine size={14} />}
              </span>
            );

            if ('ellipsis' in item) {
              return (
                <li key={`ellipsis-${idx}`} className="flex items-center gap-1">
                  <span className="px-1 text-[var(--color-fg-muted)]">…</span>
                  {!isLast ? sep : null}
                </li>
              );
            }

            const inner = item.to ? (
              <a
                href={item.to}
                onClick={item.onClick as React.MouseEventHandler<HTMLAnchorElement>}
                className={cn(
                  'truncate rounded-[var(--radius-sm)] px-1 py-0.5 hover:text-[var(--color-fg)] hover:underline',
                  isLast && 'text-[var(--color-fg)] font-medium pointer-events-none',
                )}
                data-pop
                aria-current={isLast ? 'page' : undefined}
              >
                {item.label}
              </a>
            ) : (
              <span
                onClick={item.onClick as React.MouseEventHandler<HTMLSpanElement>}
                className={cn(
                  'truncate px-1 py-0.5',
                  isLast
                    ? 'text-[var(--color-fg)] font-medium'
                    : item.onClick
                      ? 'cursor-pointer hover:text-[var(--color-fg)]'
                      : '',
                )}
                aria-current={isLast ? 'page' : undefined}
              >
                {item.label}
              </span>
            );

            return (
              <li
                key={`${idx}-${typeof item.label === 'string' ? item.label : ''}`}
                className="flex items-center gap-1 min-w-0"
              >
                {inner}
                {!isLast ? sep : null}
              </li>
            );
          })}
        </ol>
      </nav>
    );
  },
);
Breadcrumbs.displayName = 'Breadcrumbs';
